import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ImageSelectionProvider } from './contexts/ImageSelectionContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import Navbar from './components/Navigation/Navbar';
import HomePage from './pages/HomePage';
import GalleryPage from './pages/GalleryPage';
import SharedGalleryPage from './pages/SharedGalleryPage';
import AdminPage from './components/Admin/AdminPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PackagesPage from './pages/PackagesPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ClientQuotePage from './pages/ClientQuotePage';
import ReviewOptOut from './components/ReviewOptOut';
import WelcomePopup from './components/Welcome/WelcomePopup';
import SiteFooter from './components/Layout/SiteFooter';
import './App.css';
import './styles/master-consistency.css';
import './components/ui-master-styles.css';
import './components/ui-image-protection.css';
import './styles/mobile-scroll.css';
import { refreshPublicShareSettings } from './utils/publicSiteUrl';
import { MEASUREMENT_ID, installContactTracking, recordPageView, shouldRecordAnalytics } from './utils/analytics';
import { subscribeWebsiteContent } from './utils/websiteContent';
import { 
  disableRightClick, 
  disableSaveShortcuts, 
  disableTextSelection, 
  addScreenshotProtection, 
  detectDevTools, 
  initializeImageProtection 
} from './utils/ImageProtection';

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isQuotePage = location.pathname.startsWith('/quote/');
  const [showTransition, setShowTransition] = useState(false);
  const [displayLocation, setDisplayLocation] = useState(location);
  const [exitingPage, setExitingPage] = useState<typeof location | null>(null);
  
  useEffect(() => {
    const win = window as unknown as Record<string, boolean>;
    const disableKey = `ga-disable-${MEASUREMENT_ID}`;
    if (!shouldRecordAnalytics(location.pathname)) {
      win[disableKey] = true;
      return;
    }
    win[disableKey] = false;
    recordPageView(location.pathname, document.title);
  }, [location.pathname]);

  // Track route changes for slide transitions (only for homepage -> galleries)
  useEffect(() => {
    const prevPath = displayLocation.pathname;
    const currentPath = location.pathname;
    
    // Only apply slide transition when navigating from homepage to galleries
    if (prevPath === '/' && currentPath === '/galleries') {
      setExitingPage(displayLocation); // Keep old location for exit animation
      setShowTransition(true);
      
      // Start exit animation, then update to new location
      setTimeout(() => {
        setDisplayLocation(location);
        
        // Clean up after both animations complete
        setTimeout(() => {
          setShowTransition(false);
          setExitingPage(null);
        }, 600);
      }, 50); // Small delay to ensure exit animation starts
    } else {
      // No transition for other route changes
      setDisplayLocation(location);
      setShowTransition(false);
      setExitingPage(null);
    }
  }, [location.pathname, location, displayLocation]);
  
  // Scroll to top on route change (unless there's a hash for section navigation)
  useEffect(() => {
    // Only scroll to top if there's no hash in the URL
    if (!location.hash) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto' // 'auto' for immediate scroll, or 'smooth' for animated
      });
    }
  }, [location.pathname, location.hash]);
  
  useEffect(() => {
    refreshPublicShareSettings();
    installContactTracking();
    return subscribeWebsiteContent(() => undefined);
  }, []);

  // Initialize image protection
  useEffect(() => {
    // Only apply protection on non-admin pages
    if (!isAdminPage && !isQuotePage) {
      const cleanupFunctions = [
        disableRightClick(),
        disableSaveShortcuts(),
        disableTextSelection(),
        addScreenshotProtection(),
        detectDevTools(),
        initializeImageProtection()
      ];
      
      return () => {
        cleanupFunctions.forEach(cleanup => {
          if (typeof cleanup === 'function') {
            cleanup();
          }
        });
      };
    }
  }, [isAdminPage, isQuotePage]);
  
  return (
    <div className="App">
      {!isAdminPage && !isQuotePage && <Navbar />}
      <div className={`page-transition-wrapper ${showTransition ? 'transitioning' : ''}`}>
        {/* Exiting page (homepage sliding out) */}
        {exitingPage && (
          <div className="page-exiting">
            <Routes location={exitingPage}>
              <Route path="/" element={<HomePage />} />
              <Route path="*" element={null} />
            </Routes>
          </div>
        )}
        {/* Entering page (galleries sliding in) */}
        <div className="page-entering">
          <Routes location={displayLocation}>
            <Route path="/" element={<HomePage />} />
            <Route path="/galleries" element={<GalleryPage />} />
            <Route path="/galleries/:category" element={<GalleryPage />} />
            <Route path="/gallery/:id" element={<SharedGalleryPage />} />
            {/* Parked - Package Finder is now on homepage */}
            {/* <Route path="/packages" element={<PackagesPage />} /> */}
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/page/:page" element={<BlogPage />} />
            <Route path="/blog/category/:category" element={<BlogPage />} />
            <Route path="/blog/category/:category/page/:page" element={<BlogPage />} />
            <Route path="/blog/preview/:slug" element={<BlogPostPage preview />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/quote" element={<ContactPage requestQuote />} />
            <Route path="/quote/:token/pay" element={<ClientQuotePage payment />} />
            <Route path="/quote/:token" element={<ClientQuotePage />} />
            <Route path="/review-opt-out/:token" element={<ReviewOptOut />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </div>
      {!isAdminPage && !isQuotePage && <SiteFooter />}
      {!isAdminPage && location.pathname === '/' && <WelcomePopup />}
    </div>
  );
}

function App() {
  // Add cache-busting for admin pages
  React.useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.pathname.startsWith('/admin')) {
        // Force a small delay to ensure proper rendering
        setTimeout(() => {
          const navbar = document.querySelector('.navbar') as HTMLElement;
          if (navbar) {
            navbar.style.display = 'none';
            navbar.style.visibility = 'hidden';
            navbar.style.opacity = '0';
            navbar.style.height = '0';
            navbar.style.overflow = 'hidden';
          }
        }, 100);
      }
    };

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);
    
    // Check on initial load
    handleRouteChange();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <ImageSelectionProvider>
      <CurrencyProvider>
        <Router>
          <AppContent />
        </Router>
      </CurrencyProvider>
    </ImageSelectionProvider>
  );
}

export default App;
